import { MapPin, Users, Phone, MessageSquare, Star } from 'lucide-react';
import type { DashboardStats } from '@/types/leads';
import styles from './StatsGrid.module.css';

interface StatsGridProps {
  stats: DashboardStats;
  isLoading?: boolean;
}

interface StatItem {
  title: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  colorClass: string;
  description: string;
}

interface StatSection {
  label: string;
  items: StatItem[];
}

export const StatsGrid = ({ stats, isLoading }: StatsGridProps) => {
  const sections: StatSection[] = [
    {
      label: 'Leads Overview',
      items: [
        {
          title: 'Total Leads',
          value: stats.total_places,
          icon: MapPin,
          colorClass: styles.iconBlue,
          description: 'Businesses tracked',
        },
        {
          title: 'Persons Found',
          value: stats.total_persons,
          icon: Users,
          colorClass: styles.iconEmerald,
          description: 'Direct contacts',
        },
        {
          title: 'With Phone',
          value: stats.places_with_phone,
          icon: Phone,
          colorClass: styles.iconOrange,
          description: `${Math.round((stats.places_with_phone / (stats.total_places || 1)) * 100)}% of leads`,
        },
        {
          title: 'Reachable',
          value: stats.reachable_businesses,
          icon: MessageSquare,
          colorClass: styles.iconViolet,
          description: 'Phone + contact found',
        },
        {
          title: 'Interesados',
          value: stats.interested_contacts ?? 0,
          icon: Star,
          colorClass: styles.iconAmber,
          description: 'Detected interest via AI',
        },
      ],
    },
  ];

  const renderCard = (item: StatItem, loading: boolean) => (
    <div key={item.title} className={styles.card}>
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>{item.title}</span>
        <item.icon className={`${styles.icon} ${item.colorClass}`} />
      </div>
      <div className={styles.cardContent}>
        {loading ? (
          <>
            <div className={styles.skeletonValue} />
            <div className={styles.skeletonDesc} />
          </>
        ) : (
          <>
            <div className={styles.value}>{item.value}</div>
            <p className={styles.description}>{item.description}</p>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.container}>
      {sections.map((section) => (
        <div key={section.label} className={styles.section}>
          <h3 className={styles.sectionLabel}>{section.label}</h3>
          <div className={styles.grid}>
            {section.items.map((item) => renderCard(item, !!isLoading))}
          </div>
        </div>
      ))}
    </div>
  );
};
